/**
 * What the Mission Recorder's screen needs to know about a tape, worked out
 * from the tape itself.
 *
 * Split from `routes/Vcr.tsx` for the same reason every other route here has a
 * pure sibling: the questions below ("whose aeroplane is this tape about", "what
 * do I call the third Albatros") have awkward cases — a tape of a bare
 * encounter with no callsigns, a roster that has changed under a scrub — and
 * they are worth answering somewhere `bun test` can reach without a DOM.
 */

import { DISPLAY_NAMES, type AircraftKey, type MissionRegion, type ReplayTape } from "@red-opus/engine";
import type { CombatHud } from "../flight/combat-hud.ts";
import type { AircraftView } from "../flight/encounter-views.ts";

/** The HUD's combat half before the first frame: nothing seen, nothing decided. */
export const NO_CONTACT_HUD: CombatHud = {
  enemy: undefined,
  status: "active",
  banner: undefined,
  airframe: 1,
  wounded: false,
  burning: false,
  hitsScored: 0,
  hitsTaken: 0,
  gunHitsTaken: 0,
  bombs: undefined,
};

export interface TapeFlight {
  readonly aircraft: string;
  /** The fleet table's display name, for the HUD chip. */
  readonly label: string;
}

const FALLBACK_AIRCRAFT: AircraftKey = "CAMEL";

/**
 * The aeroplane the tape's own pilot was flying — which selects the cockpit
 * composition, so it has to be right even for a tape of a mission this build
 * would no longer generate the same way.
 */
export function tapeFlightOptions(tape: ReplayTape): TapeFlight {
  const key = playerAircraftOf(tape) ?? FALLBACK_AIRCRAFT;
  return { aircraft: key, label: DISPLAY_NAMES[key] };
}

/**
 * The region the tape was flown over — a mission tape's own `MissionSpec.region`,
 * or `undefined` for a bare-encounter tape (the instant dogfight has no briefed
 * region, and never has). `createFlightScene`'s `FlightSceneOptions.region`
 * reads this and falls back to `DEFAULT_FLY_REGION` (Verdun) either way, so a
 * replay shows the same sector the flight was actually recorded over.
 */
export function tapeRegion(tape: ReplayTape): MissionRegion | undefined {
  return tape.subject.kind === "mission" ? tape.subject.mission.region : undefined;
}

function playerAircraftOf(tape: ReplayTape): AircraftKey | undefined {
  if (tape.subject.kind === "mission") {
    return tape.subject.mission.flights.find((flight) => flight.id === tape.playerId)?.aircraft;
  }
  return tape.subject.encounter.aircraft.find((entry) => entry.id === tape.playerId)?.flight.aircraft;
}

/**
 * What to call each aeroplane in the tape. A mission names its flights
 * ("Werner Voss", "Recon 1"); a bare encounter does not, so the id is the
 * honest label rather than an invented one.
 */
export function tapeCallsigns(tape: ReplayTape): ReadonlyMap<string, string> {
  if (tape.subject.kind !== "mission") return new Map();
  return new Map(tape.subject.mission.flights.map((flight) => [flight.id, flight.callsign]));
}

/**
 * Everyone the camera can be switched to right now, in roster order. Read off
 * the *live* roster rather than the tape's flight list, because a scrub moves
 * which aeroplanes are in the sky and a button offering a view of one that has
 * not spawned yet would do nothing when pressed.
 */
export function viewableFrom(
  runner: { others(): readonly AircraftView[] },
  tape: ReplayTape,
): readonly { readonly id: string; readonly label: string }[] {
  const callsigns = tapeCallsigns(tape);
  return runner.others().map((entry) => ({ id: entry.id, label: callsigns.get(entry.id) ?? entry.id }));
}
